import { EmailTemplateList } from './../../email-template/model/email-template';
import { AppointmentService } from 'src/app/core/service/appointment.service';
import { SettingEditOrganizations } from './../model/settingEdit';
import { Component, OnInit, Input, Output, EventEmitter, ViewChild, ElementRef, ViewChildren, QueryList } from '@angular/core';
import { UntypedFormBuilder, Validators, UntypedFormGroup, UntypedFormControl, UntypedFormArray } from '@angular/forms';
import { SettingEdit } from '../model/settingEdit';
import { merge, reject, sortBy, cloneDeep } from 'lodash';
import { SettingValidators } from 'src/app/core/validators/setting.validators';
import { OrganizationService } from 'src/app/core/service/organization.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { TabStripComponent } from '@progress/kendo-angular-layout';
import { TranslateService } from '@ngx-translate/core';
import { TaskService } from 'src/app/core/service/task.service';
import { UserService } from 'src/app/core/service/user.service';
import { TeamService } from 'src/app/core/service/team.service';
import { TeamRole } from 'src/app/team/model/team';
import { FileRestrictions } from '@progress/kendo-angular-upload';
import { ProviderService } from 'src/app/core/service/provider.service';
import { LanguageService } from 'src/app/core/service/language.service';
import { DictionaryService } from 'src/app/core/service/dictionary.service';
import { groupBy, GroupResult } from '@progress/kendo-data-query';
import { ChannelService } from 'src/app/core/service/channel.service';
import { EmailTemplateService } from 'src/app/core/service/email-template.service';
import { MemberService } from 'src/app/core/service/member.service';
import { SettingService } from 'src/app/core/service/setting.service';
import { SessionSetting } from 'src/app/core/model/session-setting';
import { MemberListService } from 'src/app/core/service/grid-data/grid-data-member.service';
import { GridViewManager } from 'src/app/core/model/grid-view-manager';
import { AppConfigService } from 'src/app/core/service/app-config.service';
import { ColumnReorderEvent, ColumnVisibilityChangeEvent, DataStateChangeEvent } from '@progress/kendo-angular-grid';
import { GridResizeEvent } from 'src/app/core/model/grid-settings';
import { TaskListService } from 'src/app/core/service/grid-data/grid-data-task.service';
import { CaseListService } from 'src/app/core/service/grid-data/grid-data-case.service';
import { ConsentService } from 'src/app/core/service/consent.service';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';
import { ListBoxToolbarConfig } from '@progress/kendo-angular-listbox';
import { WindowComponent, WindowState } from '@progress/kendo-angular-dialog';

@Component({
  standalone: false,
  selector: 'app-setting-edit',
  templateUrl: './setting-edit.component.html',
  styleUrls: ['./setting-edit.component.scss']
})
export class SettingEditComponent implements OnInit
{
  /** Title bar, tab strip, form chrome, column headings — tuned so default 700px window ≈ 400px listbox. */
  private static readonly CLIENT_HEADER_LISTBOX_CHROME_PX = 300;

  @ViewChild('tabstrip') public tabstrip: TabStripComponent;
  @ViewChild('settingEditWindow') public settingEditWindow: WindowComponent;
  @ViewChildren(TooltipDirective) public tooltipDir: QueryList<TooltipDirective>;

  @Input() public isPerOrganization = false;
  @Input() public isNew = false;
  @Input() public assessments = [];
  @Input() public channels = [];
  @Input() public programLeadsAndProgramSupportUsers: any[] = [];
  @Input() public allUsers: any[] = [];

  @Output() public cancelling: EventEmitter<any> = new EventEmitter();
  @Output() public save: EventEmitter<SettingEdit> = new EventEmitter();

  public invalidFileExtensionText: string;
  public editForm: any;
  public editFormActive = false;
  public valueType: string;
  public intSettingValueMax = 999999;
  public enabledOrganizationsTab = false;
  public organizations = [];
  public recordContactFields = [];
  public settingKey: string;
  public emailContentTypeTexts: Array<{ text: string; value: string }> = [
    { text: 'Html', value: 'text/html' },
    { text: 'Text', value: 'text/plain' }
  ];
  public captureLinkTokenExpirationUnits: Array<{ text: string; value: string }> = [
    { text: 'Days', value: 'DAYS' },
    { text: 'Minutes', value: 'MINUTES' }
  ];
  public optInTokenExpirationUnits: Array<{ text: string; value: string }> = [
    { text: 'Days', value: 'DAYS' },
    { text: 'Hours', value: 'HOURS' }
  ];
  public consentExpirationPolicyUnits: Array<{ text: string; value: string }> = [
    { text: 'Days', value: 'DAYS' },
    { text: 'Months', value: 'MONTHS' }
  ];
  public listPriorities: Array<{ id: string; text: string; value: number }>;
  public roles: Array<{ text: string; value: string }>;
  public demographics: Array<{ text: string; value: string; key: string }>;
  public recommendedDemographics: Array<{ text: string; value: string; key: string }>;
  public schedulerViews: Array<{ text: string; value: string }>;
  public subjectIdentifiers: Array<{ displayName: string; id: string }>;
  public resourceStatuses: Array<{ displayName: string; id: string }>;
  public goalCodeSet: Array<{ name: string; id: string }>;
  public teamRoles: TeamRole[] = [];
  public imagePreview: any;
  public days: Array<{ text: string; value: string }>;
  public providerDistances: Array<{ text: string; value: string }>;
  public languages: Array<{ text: string; value: string; primary: boolean }>;
  public loginLanguages: Array<{ text: string; value: string; primary: boolean }>;
  public sendInviteTemplates: EmailTemplateList[] = [];
  public communityResources: Array<{ text: string; value: string; key: string }>;
  public searchableCommunityResources: Array<{ text: string; value: string; key: string }>;
  public insuranceFields: Array<{ text: string; value: string; key: string }>;
  public configurableDashboard: Array<{ text: string; value: string }>;
  public communityDefaultLandingPage: Array<{ text: string; value: string }>;
  public pageSizes = [5, 10, 25, 50, 100];
  public view: GridViewManager;
  public consentModels: Array<{ text: string; value: string }>;
  public consentTypes: Array<{ displayName: string; code: string; id: string }>;
  public communityServiceIdentifierTypes: Array<{ displayName: string; id: string }>;

  /** Selected sections in display/save order (same shape as `memberService.clientHeaderSections`; mutated by ListBox). */
  public chosenClientFields: Array<{ text: string; value: string; key: string }> = [];
  public clientHeaderFields: Array<{ text: string; value: string; key: string }>;

  /**
   * Toolbar tool order matters: Kendo gives only the first button `tabindex="0"` (primary tab stop).
   * Put transfer actions before move up/down so reorder is not the default and focus/keyboard
   * behavior favors moving items between lists.
   */
  public readonly clientBannerListboxToolbar: ListBoxToolbarConfig = {
    tools: ['transferTo', 'transferFrom', 'transferAllFrom', 'moveUp', 'moveDown'],
  };

  /** Window height (px); two-way with `kendo-window` so resize updates listbox height. */
  public editWindowHeightPx = 500;

  /** Height from when this edit session opened (700 client-header listbox, else 500); restored after unmaximize. */
  public initialEditWindowHeightPx = 500;

  // File restrictions
  public fileRestrictions: FileRestrictions =
    {
      allowedExtensions: ['.jpg', '.jpe', '.jpeg', '.bmp', '.png'],
      maxFileSize: 2000
    };

  public selectedFile: any = null;
  public selectedFileName: string = null;
  public selectedFileMimeType: string = null;
  public originalDocumentValue: string = null;

  /**
   * Current search term entered from a comboxbox
   */
  public searchTerm: string;

  /**
   * Filtered list of team roles for drop-down.
   */
  public teamRolesFiltered: TeamRole[] = [];
  public formAssessments: any[];
  public groupedChannels: GroupResult[] = [];
  public referralChannelSearchTerm = '';
  public groupedReferralChannels: GroupResult[] = [];
  public patientLabel: string;
  public recordContactFieldsSetting: string;

  private allReferralChannels: any[] = [];
  private referralChannelsFiltered: any[] = [];

  private settingEdit: SettingEdit;

  constructor(
    private appConfigService: AppConfigService,
    private fb: UntypedFormBuilder,
    private organizationService: OrganizationService,
    private messageHandlerService: MessageHandlerService,
    private translate: TranslateService,
    private taskService: TaskService,
    private settingValidators: SettingValidators,
    private userService: UserService,
    private teamService: TeamService,
    private memberService: MemberService,
    private appointmentService: AppointmentService,
    private providerService: ProviderService,
    private languageService: LanguageService,
    private dictionaryService: DictionaryService,
    private emailTemplateService: EmailTemplateService,
    private channelService: ChannelService,
    private settingService: SettingService,
    private memberListService: MemberListService,
    private taskListService: TaskListService,
    private caseListService: CaseListService,
    private consentService: ConsentService)
  {
    this.consentModels = consentService.consentModels;
  }

  public get clientHeaderListboxHeight(): number
  {
    return Math.max(120, this.editWindowHeightPx - SettingEditComponent.CLIENT_HEADER_LISTBOX_CHROME_PX);
  }

  public get name(): UntypedFormControl
  {
    return this.editForm.get('name');
  }

  public get value(): UntypedFormControl
  {
    return this.editForm.get('value');
  }

  public get consentExpirationPolicyUnit(): UntypedFormControl
  {
    return this.editForm.get('consentExpirationPolicyUnit');
  }

  public get captureLinkTokenExpirationUnit(): UntypedFormControl
  {
    return this.editForm.get('captureLinkTokenExpirationUnit');
  }

  public get optInTokenExpirationUnit(): UntypedFormControl
  {
    return this.editForm.get('optInTokenExpirationUnit');
  }

  public get consentExpirationPolicies(): UntypedFormControl
  {
    return this.editForm.get('consentExpirationPolicies');
  }

  public get latitude(): UntypedFormControl
  {
    return this.editForm.get('latitude');
  }

  public get longitude(): UntypedFormControl
  {
    return this.editForm.get('longitude');
  }

  @Input() public set model(settingEdit: SettingEdit)
  {
    this.formAssessments = this.assessments.filter(x => x.communityRestriction === 'NONE').sort((a, b) => a.name.localeCompare(b.name));
    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting: SessionSetting) =>
      {
        this.patientLabel = sessionSetting.patientLabel;
        this.recordContactFieldsSetting = sessionSetting.recordContactFields;
        if (settingEdit?.key && this.getValueType(settingEdit.key) === 'referralChannelsMulti')
        {
          this.channelService.getMinimalChannels('?referralOnly=true&allStatuses=true').subscribe({
            next: (channelData) =>
            {
              this.allReferralChannels = sortBy(channelData, ['name']);
              this.allReferralChannels.forEach(d => { d.programName = d.program.name; });
              this.referralChannelsFiltered = this.allReferralChannels;
              this.addMissingReferralChannelsFromValue(settingEdit.value);
              this.setGroupedReferralChannels();
              this.initModel(settingEdit);
            },
            error: (errorResponse: any) =>
            {
              this.messageHandlerService.displayError(errorResponse);
            }
          });
        }
        else
        {
          this.initModel(settingEdit);
        }
      },
      error: (errorResponse: any) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public onIntSettingNumericValueChange(control: UntypedFormControl): void
  {
    if (!control)
    {
      return;
    }
    control.markAsDirty();
    control.markAsTouched();
    control.updateValueAndValidity();
  }

  public ngOnInit(): void
  {
    this.view = new GridViewManager(this.appConfigService.getConfig().pageSize);
    this.view.setData([]);
    this.languages = this.languageService.languages.filter(x => x.primary);
    this.loginLanguages = this.languageService.languages;
    this.translate.get(['SETTING-EMAIL-TYPE-HTML', 'SETTING-EMAIL-TYPE-TEXT']).subscribe({
      next: (res) =>
      {
        let index = 0;
        for (const key in res)
        {
          if ({}.hasOwnProperty.call(res, key))
          {
            this.emailContentTypeTexts[index].text = res[key];
            index++;
          }
        }
      }
    });

    this.translate.get(['TOKEN-EXPIRATION-DAYS', 'TOKEN-EXPIRATION-MINUTES']).subscribe({
      next: (res) =>
      {
        let index = 0;
        for (const key in res)
        {
          if ({}.hasOwnProperty.call(res, key))
          {
            this.captureLinkTokenExpirationUnits[index].text = res[key];
            this.captureLinkTokenExpirationUnits[index].value = res[key].toUpperCase();
            index++;
          }
        }
      }
    });

    this.translate.get(['TOKEN-EXPIRATION-DAYS', 'TOKEN-EXPIRATION-HOURS']).subscribe({
      next: (res) =>
      {
        let index = 0;
        for (const key in res)
        {
          if ({}.hasOwnProperty.call(res, key))
          {
            this.optInTokenExpirationUnits[index].text = res[key];
            this.optInTokenExpirationUnits[index].value = res[key].toUpperCase();
            index++;
          }
        }
      }
    });

    this.translate.get(['CONSENT-EXPIRATION-POLICY-DAYS', 'CONSENT-EXPIRATION-POLICY-MONTHS']).subscribe({
      next: (res) =>
      {
        let index = 0;
        for (const key in res)
        {
          if ({}.hasOwnProperty.call(res, key))
          {
            this.consentExpirationPolicyUnits[index].text = res[key];
            this.consentExpirationPolicyUnits[index].value = res[key].toUpperCase();
            index++;
          }
        }
      }
    });

    // Get the translation used when file types is invalid
    this.translate.get('INVALID-FILE-TYPE').subscribe({
      next: (value) =>
      {
        this.invalidFileExtensionText = value;
      }
    });
    this.listPriorities = this.taskService.listPriorities;

    this.roles = sortBy(this.userService.roles.filter(x => x.value !== 'ROLE_SYSTEM_OPERATOR' && x.value !== 'ROLE_COMMUNITY_USER' &&
      x.value !== 'ROLE_COMMUNITY_ADMIN'), ['text']);

    this.demographics = sortBy(this.memberService.demographics.filter(x => x.value !== 'MIDDLE_NAME' && x.value !== 'WORK_EXTENSION' && x.value !== 'ADDRESS_LINE2' && x.value !== 'POVERTY_LEVEL'), ['text']);
    this.recommendedDemographics = sortBy(this.memberService.demographics, ['text']);
    this.clientHeaderFields = sortBy(this.memberService.clientHeaderSections, ['text']);
    this.communityResources = sortBy(this.memberService.communityResources, ['text']);
    this.insuranceFields = sortBy(this.memberService.insuranceFields, ['text']);
    for (let i = 0; i < this.communityResources.length; i++)
    {
      if (this.communityResources[i].text.includes('{{patientLabel}}'))
      {
        this.communityResources[i].text = this.communityResources[i].text.replace('{{patientLabel}}', this.patientLabel);
      }
    }
    this.searchableCommunityResources = cloneDeep(this.memberService.communityResources.filter(x => x.searchable));
    this.configurableDashboard = this.settingService.configurableDashboard;
    this.communityDefaultLandingPage = this.settingService.communityDefaultLandingPage;

    this.schedulerViews = sortBy(this.appointmentService.schedulerViews, ['text']);
    this.days = this.appointmentService.days;
    this.providerDistances = this.providerService.providerDistances;
    this.teamService.getCachedTeamRoles().subscribe({
      next: (data) =>
      {
        this.teamRoles = sortBy(data, ['name']);
        this.filterTeamRoles('');
      }
    });

    this.dictionaryService.getCachedIdentifiers().subscribe({
      next: (data) =>
      {
        this.subjectIdentifiers = sortBy(data.map(d => ({ id: d.id.toString(), displayName: d.displayName })), ['displayName']);
      }
    });

    this.dictionaryService.getResourceStatuses().subscribe({
      next: (data) =>
      {
        this.resourceStatuses = sortBy(data.map(d => ({ id: d.id.toString(), displayName: d.displayName })), ['displayName']);
      }
    });

    this.dictionaryService.getCachedCommunityServiceIdentifierTypes().subscribe({
      next: (data) =>
      {
        this.communityServiceIdentifierTypes = sortBy(data.map(d => ({ id: d.id.toString(), displayName: d.displayName })), ['displayName']);
      }
    });

    // Load CodeSets
    this.dictionaryService.getCachedMinimalCodeSets().subscribe({
      next: (data) =>
      {
        this.goalCodeSet = sortBy(data.map(d => ({ id: d.id.toString(), name: d.name })), ['name']);
      },
      error: (error) =>
      {
        this.messageHandlerService.displayError(error);
      }
    });

    this.emailTemplateService.getCachedEmailTemplatesList('CommunityInvite').subscribe({
      next: (data) =>
      {
        this.sendInviteTemplates = data;
      },
      error: errorResponse =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });

    this.dictionaryService.getCachedConsentTypes().subscribe({
      next: (data) =>
      {
        this.consentTypes = sortBy(data.map(d => ({ id: d.id.toString(), code: d.code, displayName: d.displayName })), ['displayName']).filter(x => x.code !== 'community' && x.code !== 'exclusion');
      }
    });
  }

  /**
   * Maximize/minimize/restore updates the window height in Kendo without always emitting `heightChange`,
   * so `[(height)]` can stay stale — copy from `WindowComponent` after layout settles.
   */
  public onSettingEditWindowStateChange(state: WindowState): void
  {
    if (state === 'default')
    {
      this.scheduleRestoreInitialWindowHeight();
      return;
    }
    this.scheduleSyncEditWindowHeightFromWindow();
  }

  public syncEditWindowHeightFromWindow(): void
  {
    const w = this.settingEditWindow;
    if (w && typeof w.height === 'number' && !Number.isNaN(w.height))
    {
      this.editWindowHeightPx = w.height;
    }
  }

  /**
   * Kendo ListBox does not implement ControlValueAccessor — push selected section ids into `value` and mark dirty.
   */
  public onClientBannerListboxAction(): void
  {
    if (this.isClientHeaderFields(this.valueType))
    {
      // ListBox mutates `data` order on transfer; keep the catalog side alphabetical.
      this.clientHeaderFields = sortBy(this.clientHeaderFields, ['text']);
      if (this.value)
      {
        const ids = this.chosenClientFields.map((item) => item.value);
        this.value.setValue(ids, { emitEvent: false });
      }
    }
    this.editForm.markAsDirty();
  }

  /**
   * Save handler fires when user hit save.
   *
   * @param event Event
   */
  public onSave(event: Event): void
  {
    event.preventDefault();
    const objectToPersist = merge(this.settingEdit, this.editForm.value);
    delete objectToPersist.name;            // No need to post name to server since used for UI only
    delete objectToPersist.lastModifiedOn;

    // Make we don't have the same Consent Type selected twice
    let consentError = false;
    if (objectToPersist.consentExpirationPolicies.length > 0)
    {
      const orgs = [];
      objectToPersist.consentExpirationPolicies.forEach((obj) =>
      {
        if (!orgs.includes(obj.consentType.id))
        {
          orgs.push(obj.consentType.id);
        }
        else
        {
          this.messageHandlerService.displayError('SETTING-MORE-THAN-ONE-CONSENT-TYPE');
          consentError = true;
        }
      });
    }

    // Make we don't have the same organization selected twice
    let error = false;
    if (objectToPersist.organizations.length > 0)
    {
      const orgs = [];
      objectToPersist.organizations.forEach((obj: SettingEditOrganizations) =>
      {
        if (!orgs.includes(obj.organization.id))
        {
          orgs.push(obj.organization.id);
        }
        else
        {
          this.tabstrip.selectTab(1);
          this.messageHandlerService.displayError('SETTING-MORE-THAN-ONE-ORGANIZATION');
          error = true;
        }
      });
    }

    if (error || consentError)
    {
      return;
    }

    // Check if the value is an array, if it is an array transform to string
    if (objectToPersist.value instanceof Array)
    {
      objectToPersist.value = objectToPersist.value.join();
      objectToPersist.organizations.forEach((obj: SettingEditOrganizations) =>
      {
        obj.value = obj.value.join();
      });
    }

    // If latitude/longitude
    if (this.isLatitudeLongitudeValue(this.getValueType(this.settingEdit.key)))
    {
      objectToPersist.value = `${this.latitude.value},${this.longitude.value}`;
    }

    // If image setting
    if (this.isImageValue(this.getValueType(this.settingEdit.key)))
    {
      if (this.selectedFile)
      {
        const base64 = this.selectedFile.split('base64,')[1];
        objectToPersist.documentContent = base64;
        objectToPersist.documentName = this.value.value;
        objectToPersist.documentMimeType = this.selectedFileMimeType;
        objectToPersist.value = this.originalDocumentValue;
      }
      else
      {
        objectToPersist.documentContent = null;
        objectToPersist.documentName = null;
        objectToPersist.documentMimeType = null;
        objectToPersist.value = this.originalDocumentValue;
      }
    }

    // If filter setting
    if (this.isGridFilterValue(this.getValueType(this.settingEdit.key)))
    {
      objectToPersist.filterState = JSON.stringify(this.view.state);
      objectToPersist.filterColumns = JSON.stringify(this.view.columnsConfig);
    }

    // Reset if value is not defined or null
    if (this.editForm.value.value === undefined || this.editForm.value.value === null)
    {
      objectToPersist.value = '';
    }

    // Emit save event and hide form.
    this.save.emit(objectToPersist);
    this.editFormActive = false;
  }

  /**
   * Cancel event handler.
   *
   * @param event Event.
   */
  public onCancel(event: Event): void
  {
    event.preventDefault();
    this.closeEditForm();
  }

  /**
   * Remove organization setting entry.
   *
   * @param i Row index
   * @param id Setting id
   */
  public removeOrganization(i: number, id: number): void
  {
    const control = this.editForm.controls.organizations;
    // If this id was already persisted, we must remove
    if (id > 0)
    {
      const rejectedId: any = {};
      rejectedId.id = id;
      this.settingEdit.organizations = reject(this.settingEdit.organizations, rejectedId);
    }
    control.removeAt(i);
    this.editForm.markAsDirty();
  }

  /**
   * Check if drop down item has been disabled.
   *
   * @param itemArgs Drop down item.
   */
  public itemInactive(itemArgs: { dataItem: any; index: number }): boolean
  {
    return !itemArgs.dataItem.active;
  }

  /**
   * Check if drop down item has been disabled.
   *
   * @param itemArgs Drop down item.
   */
  public itemDisabled(itemArgs: { dataItem: any; index: number }): boolean
  {
    return !itemArgs.dataItem.enabled;
  }

  /**
   * Check if drop down item has been disabled.
   *
   * @param itemArgs Drop down item.
   */
  public demographicItemDisabled(itemArgs: { dataItem: any; index: number }): boolean
  {
    // For v1, disable the four main properties. If we want to open in the future, more analysis is required to evaluate impacts.
    return itemArgs.dataItem.value === 'FIRST_NAME' ||
      itemArgs.dataItem.value === 'LAST_NAME' ||
      itemArgs.dataItem.value === 'DATE_OF_BIRTH';
  }

  /**
   * Check if drop down item has been disabled.
   *
   * @param itemArgs Drop down item.
   */
  public communityResourceItemDisabled(itemArgs: { dataItem: any; index: number }): boolean
  {
    // For v1, disable the THREE main properties. If we want to open in the future, more analysis is required to evaluate impacts.
    return itemArgs.dataItem.value === 'ORGANIZATION' ||
      itemArgs.dataItem.value === 'SERVICE_CATEGORY' ||
      itemArgs.dataItem.value === 'NAME';
  }

  /**
   * Check if drop down item has been disabled.
   *
   * @param itemArgs Drop down item.
   */
  public insuranceFieldItemDisabled(itemArgs: { dataItem: any; index: number }): boolean
  {
    return itemArgs.dataItem.value === 'INSURER';
  }

  public searchableCommunityResourceItemDisabled(itemArgs: { dataItem: any; index: number }): boolean
  {
    return itemArgs.dataItem.value === 'SERVICE_CATEGORY';    // For Referral, must not disable the service category
  }

  /**
   * File has been selected.
   *
   * @param event Event
   */
  public onFileChanged(event: Event): void
  {
    const input = event.target as HTMLInputElement;
    const fileList: FileList = input.files;
    if (fileList.length > 0)
    {
      const file: File = fileList[0];

      // Validate file size
      if ((file.size / 1024) > this.fileRestrictions.maxFileSize)
      {
        this.messageHandlerService.displayError('FILE-SIZE-EXCEED-LIMIT');
        return;
      }

      // Validate file extension
      let validExtension = false;
      this.fileRestrictions.allowedExtensions.forEach((ext: string) =>
      {
        if (file.name.toLowerCase().indexOf(ext) !== -1)
        {
          validExtension = true;
        }
      });
      if (!validExtension)
      {
        this.messageHandlerService.displayDirectMessage('error',
          this.invalidFileExtensionText.replace('{{value}}', this.fileRestrictions.allowedExtensions.join()));
        return;
      }
      this.selectedFileName = file.name;
      this.selectedFileMimeType = file.type;
      this.value.setValue(file.name);
      this.editForm.markAsDirty();
      this.setFileFromBlob(file);
    }
  }

  /**
   * Filters the team roles on user input.
   */
  public filterTeamRoles(filter: string): void
  {
    this.searchTerm = filter;
    this.teamRolesFiltered = this.teamRoles
      .filter(user => user.name.toLowerCase().includes(filter.toLowerCase()));
  }

  /**
   * Check if team role from dropdown is disabled or enabled.
   *
   * @returns boolean
   */
  public roleItemDisabled = (itemArgs: { dataItem: any; index: number }): boolean =>
    !this.settingService.isValidSettingForRole(this.settingKey, itemArgs.dataItem.value);

  /**
   * Check if item from dropdown is disabled or enabled.
   *
   * @returns boolean
   */
  public channelItemDisabled(itemArgs: { dataItem: any; index: number }): boolean
  {
    return itemArgs.dataItem.status !== 'Online';
  }

  public filterReferralChannels(filter: string): void
  {
    this.referralChannelSearchTerm = filter;
    this.referralChannelsFiltered = this.allReferralChannels
      .filter(channel => channel.name.toLowerCase().includes(filter.toLowerCase()));
    this.setGroupedReferralChannels();
  }

  public deleteImage(): void
  {
    this.imagePreview = null;
    this.selectedFile = null;
    this.value.setValue(null);
    this.editForm.markAsDirty();
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.view.setState(state);
    this.editForm.markAsDirty();
  }

  /**
   * Grid column visibility changed.
   *
   * @param event Grid column visibility event.
   */
  public onVisibilityChange(event: ColumnVisibilityChangeEvent): void
  {
    event.columns.forEach((column: any) =>
    {
      const field = this.view.columnsConfig.find((col) => col.field === column.field);
      if (field !== undefined)
      {
        field.hidden = column.hidden;
      }
    });
    this.editForm.markAsDirty();
  }

  /**
   * Grid onResize event.
   *
   * @param event Grid on resize column event.
   */
  public onResize(event: Array<GridResizeEvent>): void
  {
    event.forEach(item =>
    {
      const field = this.view.columnsConfig.find((col) => col.field === item.column.field);
      if (field !== undefined)
      {
        field.width = item.newWidth;
      }
    });
    this.editForm.markAsDirty();
  }

  /**
   * Reorder grid column order event
   *
   * @param e ColumnReorderEvent event
   */
  public onReorder(e: ColumnReorderEvent): void
  {
    const reorderedColumn = this.view.columnsConfig.splice(e.oldIndex, 1);
    this.view.columnsConfig.splice(e.newIndex, 0, ...reorderedColumn);
    this.editForm.markAsDirty();
  }

  public pageChange(event: any): void
  {
    this.view.state.take = event.take;
    this.editForm.markAsDirty();
  }

  public addConsentExpirationPolicy(id: number = null, consentType: any = null, consentExpirationPolicyValue: number = null,
    unit: any = null, isLoading = false): void
  {
    this.editForm.controls.consentExpirationPolicies.push(this.initConsentExpirationPolicy(id, consentType, consentExpirationPolicyValue, unit));
    if (!isLoading)
    {
      this.editForm.markAsDirty();
    }
  }

  public removeConsentExpirationPolicy(i: number, id: number): void
  {
    const control = this.editForm.controls.consentExpirationPolicies;
    // If this id was already persisted, we must remove
    if (id > 0)
    {
      const rejectedId: any = {};
      rejectedId.id = id;
      this.settingEdit.consentExpirationPolicies = reject(this.settingEdit.consentExpirationPolicies, rejectedId);
    }
    control.removeAt(i);
    this.editForm.touched = true;
    this.editForm.markAsDirty();
  }

  public showTooltip(e: ElementRef, tooltip: ElementRef): void
  {
    const tooltipDirective = this.tooltipDir.toArray().find(t => t.tooltipWrapper.nativeElement === tooltip);
    if (tooltipDirective)
    {
      tooltipDirective.toggle(e, true); // Show the tooltip
    }
  }

  public hideTooltip(tooltip: ElementRef): void
  {
    const tooltipDirective = this.tooltipDir.toArray().find(t => t.tooltipWrapper.nativeElement === tooltip);
    if (tooltipDirective)
    {
      tooltipDirective.hide(); // Show the tooltip
    }
  }

  /**
   * Close edit form handler and emit cancel event.
   */
  private closeEditForm(): void
  {
    this.editFormActive = false;
    this.cancelling.emit();
  }

  /**
   * Model initializer.
   *
   * @param settingEdit Edit model.
   */
  private initModel(settingEdit: SettingEdit): void
  {
    if (settingEdit === undefined)
    {
      return;
    }

    this.getOrganizations();

    this.enabledOrganizationsTab = this.isPerOrganization;
    this.valueType = this.getValueType(settingEdit.key);
    this.originalDocumentValue = settingEdit.value;

    // Set form
    this.editForm = this.fb.group({
      id: [settingEdit.id],
      name: [
        { value: settingEdit.name, disabled: true }
      ],
      value: this.getValue(settingEdit.key, settingEdit.value, settingEdit),
      consentExpirationPolicyUnit: this.getConsentExpirationPolicyUnit(settingEdit.value),
      captureLinkTokenExpirationUnit: [{ value: this.getTokenExpiration(settingEdit.value, false), disabled: false }, { validators: settingEdit.key === 'LinkTokenExpirationMinutes' ? Validators.required : [] }],
      optInTokenExpirationUnit: [{ value: this.getTokenExpiration(settingEdit.value, false), disabled: false }, { validators: settingEdit.key === 'OptInTokenExpiration' ? Validators.required : [] }],
      consentExpirationPolicies: this.fb.array([]),
      organizations: this.fb.array([]),
      recordContactFields: this.fb.array([]),
      latitude: null,
      longitude: null
    });

    this.convertRecordContactToList(this.recordContactFieldsSetting);
    if (settingEdit.key === 'ConsentExpiration')
    {
      this.convertConsentExpirationPolictToList(settingEdit.value);
    }

    if (this.valueType === 'latitudeLongitude')
    {
      const [lat, lon] = this.parseLatitudeLongitudeString(this.value.value);
      this.latitude.setValue(lat);
      this.longitude.setValue(lon);
    }

    if (this.isClientHeaderFields(this.valueType))
    {
      const catalog = sortBy(this.memberService.clientHeaderSections, ['text']);
      const catalogByValue = new Map(catalog.map((item) => [item.value, item]));
      const rawIds = Array.isArray(this.value.value) ? (this.value.value as string[]) : [];
      const seen = new Set<string>();
      const orderedIds: string[] = [];
      for (const id of rawIds)
      {
        if (!seen.has(id))
        {
          seen.add(id);
          orderedIds.push(id);
        }
      }
      // Preserve saved order (not catalog sort order); drop unknown ids.
      this.chosenClientFields = orderedIds
        .map((id) => catalogByValue.get(id))
        .filter((item): item is { text: string; value: string; key: string } => item != null);
      const chosenSet = new Set(this.chosenClientFields.map((x) => x.value));
      this.clientHeaderFields = catalog.filter((item) => !chosenSet.has(item.value));
    }
    else
    {
      this.chosenClientFields = [];
    }

    this.settingKey = settingEdit.key;  // Store current settingKey and being used for organization settings.
    const base64 = settingEdit.documentContent;
    if (base64)
    {
      this.imagePreview = `data:image/jpeg;base64,${base64}`;
    }
    else
    {
      this.imagePreview = null;
    }

    // Create form elements for setting per organizations
    settingEdit.organizations.forEach(data =>
    {
      this.addOrganization(data.id, data.organization, data.value);
    });

    this.settingEdit = settingEdit;
    this.initialEditWindowHeightPx = this.isClientHeaderFields(this.valueType) ? 700 : 500;
    this.editWindowHeightPx = this.initialEditWindowHeightPx;
    this.editFormActive = settingEdit !== undefined;
  }

  private getTokenExpiration(value: any, returnValue: boolean): any // returnValue: true return numeric value, false return unit (minutes or days)
  {
    if (value !== null)
    {
      const unit = value.charAt(value.length - 1);
      const intValue = value.slice(0, -1);
      if (returnValue)
      {
        return parseInt(intValue, 10);
      }
      else
      {
        switch (unit)
        {
          case 'M':
            return this.translate.instant('TOKEN-EXPIRATION-MINUTES').toUpperCase();
          case 'D':
            return this.translate.instant('TOKEN-EXPIRATION-DAYS').toUpperCase();
          case 'H':
            return this.translate.instant('TOKEN-EXPIRATION-HOURS').toUpperCase();
        }
      }
    }
  }

  private getConsentExpirationPolicyUnit(value: any): any // return unit (months or days)
  {
    if (value !== null)
    {
      // Find the portion that starts with "D|" (default value) until the next comma
      const dIndex = value.indexOf('D|');
      if (dIndex !== -1)
      {
        const afterD = value.substring(dIndex);
        const commaIndex = afterD.indexOf(',');
        const defaultValue = commaIndex !== -1 ? afterD.substring(0, commaIndex) : afterD;
        const unit = defaultValue.charAt(defaultValue.length - 1);
        switch (unit)
        {
          case this.translate.instant('CONSENT-EXPIRATION-POLICY-MONTHS').charAt(0):
            return this.translate.instant('CONSENT-EXPIRATION-POLICY-MONTHS').toUpperCase();
          case this.translate.instant('CONSENT-EXPIRATION-POLICY-DAYS').charAt(0):
            return this.translate.instant('CONSENT-EXPIRATION-POLICY-DAYS').toUpperCase();
        }
      }
    }
  }

  /**
   * Provide the value in the numeric format based on setting and set the proper validator.
   *
   * @param key Setting Key.
   * @param value Setting Value.
   */
  private getValue(key: string, value: any, setting: SettingEdit): Array<any>
  {
    const valueType = this.getValueType(key);
    const valueValidators = [];
    let valueDisabled = false;

    if (this.isSettingRequiredValue(key, valueType))
    {
      valueValidators.push(Validators.required);
    }

    if (this.isIntValue(valueType) || this.isIntSettingValueMaxValueType(valueType))
    {
      this.addIntSettingMaxValidators(valueValidators);
    }

    if ((key === 'LinkTokenExpirationMinutes') || (key === 'OptInTokenExpiration'))
    {
      value = this.getTokenExpiration(value, true);
      return [{ value, disabled: valueDisabled }, Validators.compose(valueValidators)];
    }
    else
      if (key === 'ConsentExpiration')
      {
        const defaultValue = value.split(',')[0];
        const consentType = defaultValue.split('|')[0];
        if (consentType === this.translate.instant('DEFAULT').charAt(0))
        {
          value = defaultValue.split('|')[1];
        }
        value = value ? parseInt(value, 10) : 0;
        return [{ value, disabled: valueDisabled }, Validators.compose(valueValidators)];
      }

    if (this.isIntValue(valueType) || this.isTeamRolesValue(valueType) || this.isAssessmentsValue(valueType) ||
      this.isSendInviteTemplateValue(valueType))
    {
      value = value ? parseInt(value, 10) : 0;
    }
    else if (this.isGridFilterValue(valueType))
    {
      value = value ? parseInt(value, 10) : 0;
      this.initGridColumns(key, setting.filterState, setting.filterColumns);
    }
    else if (this.isChannelsValue(valueType))
    {
      value = value ? parseInt(value, 10) : 0;

      this.channels.forEach(d =>
      {
        d.programName = d.program.name;
      });

      this.setGroupedChannels();

      if (value !== 0)
      {
        const channelFound = this.channels.filter(x => x.id === value);
        if (channelFound.length === 0)
        {
          this.channelService.getMinimalChannelById(value).subscribe({
            next: (lookupChannel) =>
            {
              if (lookupChannel.length === 1)
              {
                lookupChannel.forEach(d =>
                {
                  d.programName = d.program.name;
                });

                // Add Channel to allChannels array to show but will not be able to select (will be disabled)
                this.channels.push(lookupChannel[0]);
                this.setGroupedChannels();
              }
              else
              {
                // Skip the load since we can't retrieve the channelId.
                this.messageHandlerService.displayWarning('CHANNEL-NOT-FOUND');
              }
            }
          });
        }
      }
    }
    else if (this.isPatternRegEx(key))
    {
      valueValidators.push(this.settingValidators.validateRegExMinMax('$min'));
      valueValidators.push(this.settingValidators.validateRegExMinMax('$max'));
    }
    else if (this.isArrayValue(valueType))
    {
      value = value !== '' && value !== null ? value.split(',') : [];
      // Must convert array of string ids to array of numbers
      if ((this.isProgramLeadUsersValue(valueType) || this.isUsersValue(valueType) ||
        this.isTeamRolesMultiValue(valueType) || this.isAssessmentsMultiValue(valueType) ||
        this.isReferralChannelsMultiValue(valueType)) && value.length > 0)
      {
        const arrayNumbers = [];
        value.forEach((id: string) =>
        {
          arrayNumbers.push(parseInt(id, 10));
        });
        value = arrayNumbers;
      }
    }
    else if (this.isBooleanValue(valueType))
    {
      value = value === 'true';
    }
    else if (this.isImageValue(valueType))
    {
      value = setting.documentName;
      valueDisabled = true;
    }
    // eslint-disable-next-line object-shorthand
    return [{ value: value, disabled: valueDisabled }, Validators.compose(valueValidators)];
  }

  /**
   * Add organization setting to editForm
   *
   * @param id Setting id.
   * @param organization Organization.
   * @param value Setting value.
   */
  private addOrganization(id: number = null, organization: number = null, value: string = null): void
  {
    this.editForm.controls.organizations.push(this.initOrganization(id, organization, value));
  }

  /**
   * Create the organization setting form object.
   *
   * @param id Setting id.
   * @param organization Organization.
   * @param value Setting value.
   */
  private initOrganization(id: number = null, organization: any = null, value: string = null): UntypedFormGroup
  {
    return this.fb.group({
      id: [id],
      organization: [organization, Validators.required],
      value: this.getValue(this.settingKey, value, null),
    });
  }

  private addRecordContactField(name: any, visible: boolean, required: boolean): void
  {
    const field = this.editForm.get('recordContactFields') as UntypedFormArray;
    this.editForm.controls.recordContactFields.push(this.initRecordContactField(name, visible, required));

    // Register changes when clicking "Visible"
    const controlIndex = this.editForm.controls.recordContactFields.controls.length - 1;
    this.editForm.controls.recordContactFields.controls[controlIndex].get('valueVisible').valueChanges.subscribe({
      next: (visible: boolean) =>
      {
        this.valueChanges(controlIndex, visible);
      }
    });
    this.valueChanges(controlIndex, field.controls[controlIndex].get('valueVisible').value);
  }

  private valueChanges(controlIndex: number, visible: boolean): void
  {
    if (visible === true)
    {
      this.editForm.controls.recordContactFields.controls[controlIndex].get('valueRequired').enable();
    }
    else
      if (visible === false)
      {
        this.editForm.controls.recordContactFields.controls[controlIndex].get('valueRequired').disable();
        this.editForm.controls.recordContactFields.controls[controlIndex].get('valueRequired').setValue(null);
      }
    this.editForm.controls.recordContactFields.controls[controlIndex].get('valueRequired').updateValueAndValidity();
  }

  private initRecordContactField(name: any, visible: boolean, required: boolean): UntypedFormGroup
  {
    return this.fb.group({
      name: [name],
      valueVisible: [visible],
      valueRequired: [{ value: required, disabled: visible === false ? true : false }],
    });
  }

  private initConsentExpirationPolicy(id: number, consentType: any, consentExpirationPolicyValue: number, unit: any): UntypedFormGroup
  {
    const group: any = {};
    group.id = [id];
    group.consentType = [consentType, Validators.required];
    group.consentExpirationPolicyValue = [
      consentExpirationPolicyValue,
      Validators.compose([Validators.required, Validators.max(this.intSettingValueMax)])
    ];
    group.unit = [unit, Validators.required];
    return this.fb.group(group);
  }

  /**
   * Get all organizations
   */
  private getOrganizations(): void
  {
    this.organizationService.getCachedOrganizations().subscribe({
      next: (data) =>
      {
        this.organizations = data;
      },
      error: errorResponse =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  /**
   * Get the value type of the setting. This control the form control to provide.
   *
   * @param key Setting key.
   * @returns string
   */
  private getValueType(key: string): string
  {
    let valueType: string;
    switch (key.toUpperCase())
    {
      case 'REGISTRATIONTOKENEXPIRATIONHOURS':
      case 'FORGOTPASSWORDTOKENEXPIRATIONHOURS':
      case 'USERNAMEMIN':
      case 'USERNAMEMAX':
      case 'PASSWORDMIN':
      case 'PASSWORDMAX':
      case 'PASSWORDMINSERVICE':
      case 'PASSWORDMAXSERVICE':
      case 'USERIMAGEMAXKB':
      case 'MAXLOGINATTEMPT':
      case 'SESSIONIDLE':
      case 'SESSIONTIMEOUT':
      case 'MODELERAUTOSAVE':
      case 'DISABLEACCOUNTSOPERATORSDAYS':
      case 'DISABLEACCOUNTSNONOPERATORSDAYS':
      case 'CHANNELGOALMAXDAYS':
      case 'NOTICEEVENTSHOURSFILTER':
      case 'EVENTPASTDAYSLIMIT':
      case 'SCHEDULERAGENDADAYS':
      case 'SCHEDULERMULTIDAYS':
      case 'SCHEDULERAPPOINTMENTFOLLOWUP':
      case 'SCHEDULERAPPOINTMENTREMINDER':
      case 'CONSENTINVITETOKENEXPIRATIONDAYS':
      case 'PINEXPIRATIONMINUTES':
      case 'COMMUNITYPROVIDERMAXIMUMDISTANCE':
      case 'SENDINVITEDEFAULTVALIDDAYS':
      case 'APPOINTMENTSCOUNTDAYS':
      case 'ATTACHMENTSCOUNTDAYS':
      case 'INSURANCESCOUNTDAYS':
      case 'MESSAGESCOUNTDAYS':
      case 'NOTESCOUNTDAYS':
      case 'ASSESSMENTSCOUNTDAYS':
      case 'CONTACTSHISTORYCOUNTDAYS':
      case 'EXTRACTLIMIT':
      case 'RECENTCASESTOSHOW':
      case 'RECENTPATIENTSTOSHOW':
      case 'DELETEOLDPASSWORDSDAYS':
      case 'PASSWORDEXPIRATIONDAYS':
      case 'PASSWORDEXPIRATIONDAYSWARNING':
        valueType = 'int';
        break;
      case 'LINKTOKENEXPIRATIONMINUTES':
        valueType = 'linkTokenExpiration';
        break;
      case 'OPTINTOKENEXPIRATION':
        valueType = 'optInTokenExpiration';
        break;
      case 'CONSENTEXPIRATION':
        valueType = 'consentExpirationPolicy';
        break;
      case 'CONSENTMODEL':
        valueType = 'consentModel';
        break;
      case 'COMMUNITYDEFAULTLANDINGPAGE':
        valueType = 'communityDefaultLandingPage';
        break;
      case 'EMAILCONTENTTYPE':
        valueType = 'emailContentType';
        break;
      case 'TASKPRIORITY':
        valueType = 'priority';
        break;
      case 'PASSWORDPATTERNTEXTEN':
      case 'PASSWORDPATTERNTEXTES':
      case 'PASSWORDPATTERNTEXTDE':
      case 'APPLICATIONNAME':
      case 'COMMUNITYSELFSURVEYASSESSMENTSUPPORT':
      case 'COMMUNITYSELFSURVEYASSESSMENTINSTRUCTIONS':
        valueType = 'textarea';
        break;
      case 'KNO2ENABLED':
      case 'ONERECORDENABLED':
      case 'COMMUNITYENABLED':
      case 'COMMUNITYPORTALENABLED':
      case 'COMMUNITYCONSENTENABLED':
      case 'DISPLAYSETTINGSMENUCARD':
      case 'PATIENTIMPORTMATCHNAMESANDDOB':
      case 'SUBJECTADDRESSREQUIRED':
      case 'SENDEMAILTEMPLATELOGO':
      case 'CARETEAMACCOUNTREGISTRATIONENABLED':
      case 'PATIENTRESTRICTIONSENABLED':
      case 'ALLOWMULTIPLECHANNELSENROLLMENT':
      case 'ALLOWMULTIPLEDASHBOARDENROLLMENT':
      case 'PAGINGRESTRICTIONPATIENTENABLED':
      case 'PAGINGRESTRICTIONCASEENABLED':
      case 'PAGINGRESTRICTIONTASKENABLED':
      case 'SHOWTRIBALAFFILIATIONS':
      case 'SHOWTRIBALENROLLMENTS':
      case 'SHOWDEPENDENTS':
      case 'ALLOWALTERNATIVETWOSTEPAUTHENABLED':
      case 'TWOSTEPVERIFICATIONENABLEDWHENCREATINGNEWUSER':
      case 'EHRSHOWASSESSMENTS':
      case 'PATIENTDEMOGRAPHICSENABLEFAX':
      case 'PATIENTDEMOGRAPHICSENABLEPAGER':
      case 'PATIENTDEMOGRAPHICSENABLEWEBSITE':
      case 'AUTOFITGRIDCOLUMNSPATIENT':
      case 'AUTOFITGRIDCOLUMNSTASK':
      case 'AUTOFITGRIDCOLUMNSCASE':
      case 'ALLOWFREETEXTFORNEWGOALS':
      case 'SHOWGENDER':
      case 'SHOWADMINISTRATIVEGENDER':
      case 'SHOWTASKNOTESONCLIENTPROFILE':
        valueType = 'boolean';
        break;
      case 'REPORTROLES':
      case 'PATIENTEDITROLES':
      case 'WORKFLOWSTATUSEDITROLES':
      case 'SENSITIVITIESEDITROLES':
      case 'COMMUNITYSERVICESEARCHROLES':
      case 'CONTACTSROLES':
      case 'APPOINTMENTSROLES':
      case 'INSURANCESROLES':
      case 'ATTACHMENTSROLES':
      case 'MESSAGESROLES':
      case 'CHATROLES':
      case 'NOTESROLES':
      case 'EVENTSROLES':
      case 'PINNEDRESOURCESROLES':
      case 'WELLBEINGROLES':
      case 'OTHERCASESROLES':
      case 'REFERRALSROLES':
      case 'CASEHISTORYROLES':
      case 'CASEEXTENSIONSROLES':
      case 'CASECARETEAMSROLES':
      case 'PATIENTCARETEAMSROLES':
      case 'PROVIDERSROLES':
      case 'ANALYTICSROLES':
      case 'PATIENTGOALSROLES':
      case 'CASEGOALSROLES':
      case 'AUDITMEMBERROLES':
      case 'ADDUPDATECOMMUNITYRESOURCESROLES':
      case 'ALLOWORGANIZATIONUSERSROLES':
      case 'ALLOWORGANIZATIONGROUPSROLES':
      case 'ADDUPDATECHANNELCATEGORIESROLES':
      case 'ADDUPDATEDICTIONARIESROLES':
      case 'ADDUPDATEEXTENSIONSROLES':
      case 'ADDUPDATEFIELDCONFIGURATIONSROLES':
      case 'ADDUPDATEMETRICSROLES':
      case 'ADDUPDATEPROGRAMCATEGORIESROLES':
      case 'ADDUPDATESERVICECATEGORIESROLES':
      case 'ADDUPDATETERMSANDCONDITIONSROLES':
      case 'ADDUPDATEASSESSMENTSROLES':
      case 'ADDUPDATEASSESSMENTTYPESROLES':
      case 'ADDUPDATEPROGRAMTEMPLATESROLES':
      case 'ADDUPDATEFACILITIESROLES':
      case 'ADDUPDATECLIENTIMPORTROLES':
      case 'ADDUPDATEPROVIDERSROLES':
      case 'ADDUPDATETEAMROLESROLES':
      case 'ADDUPDATEINSURANCESROLES':
      case 'DELETEASSESSMENTSROLES':
      case 'ADDCASEASSESSMENTSROLES':
      case 'ADDCLIENTASSESSMENTSROLES':
      case 'ADDCASEREFERRALSROLES':
      case 'ADDCLIENTREFERRALSROLES':
      case 'CONTACTSCONTACTEDONROLES':
      case 'ADDUPDATEAUTHORIZEDORGANIZATIONSROLES':
      case 'UPDATESUBJECTIDENTIFIERSROLES':
      case 'CLIENTSEARCHONLYROLES':
        valueType = 'roles';
        break;
      case 'REQUIREDDEMOGRAPHICS':
        valueType = 'demographics';
        break;
      case 'RECOMMENDEDDEMOGRAPHICS':
        valueType = 'recommendedDemographics';
        break;
      case 'RECORDCONTACTFIELDS':
        valueType = 'recordContactFieldsSetting';
        break;
      case 'SEARCHABLECOMMUNITYRESOURCES':
        valueType = 'searchableCommunityResources';
        break;
      case 'REQUIREDCOMMUNITYRESOURCES':
        valueType = 'communityResources';
        break;
      case 'REQUIREDINSURANCEFIELDS':
        valueType = 'insuranceFields';
        break;
      case 'WORKFLOWRESTRICTEDUSERS':
      case 'REPORTRESTRICTEDUSERS':
        valueType = 'programLeads';
        break;
      case 'EHRVIEWUSERS':
        valueType = 'users';
        break;
      case 'SCHEDULERVIEWS':
        valueType = 'schedulerViews';
        break;
      case 'SUBJECTIDENTIFIERS':
        valueType = 'subjectIdentifiers';
        break;
      case 'DEFAULTRESOURCESTATUSES':
        valueType = 'resourceStatuses';
        break;
      case 'PAYERBILLINGIDENTIFIER':
        valueType = 'payerBillingIdentifier';
        break;
      case 'CONSENTABLEOPTIONSDISABLEDREADONLYONINSERT':
        valueType = 'consentTypes';
        break;
      case 'GOALDICTIONARY':
        valueType = 'goalCodeSet';
        break;
      case 'PROVIDERTEAMROLEREFERRING':
      case 'PROVIDERTEAMROLEREFERREDTO':
        valueType = 'teamRoles';
        break;
      case 'PATIENTCONTACTTEAMS':
        valueType = 'teamRolesMulti';
        break;
      case 'SCHEDULERSTARTWEEKDAY':
      case 'SCHEDULERENDWEEKDAY':
        valueType = 'day';
        break;
      case 'TEMPLATELOGODOCUMENTID':
      case 'REPORTLOGODOCUMENTID':
      case 'BRANDLOGODOCUMENTID':
      case 'HEADERLOGODOCUMENTID':
        valueType = 'image';
        break;
      case 'PROVIDERDISTANCEDISPLAY':
        valueType = 'distance';
        break;
      case 'COMMUNITYMAPLATITUDELONGITUDE':
        valueType = 'latitudeLongitude';
        break;
      case 'SYSTEMLANGUAGE':
        valueType = 'language';
        break;
      case 'LOGINLANGUAGES':
        valueType = 'loginLanguages';
        break;
      case 'COMMUNITYASSESSMENTIDS':
        valueType = 'assessmentsMulti';
        break;
      case 'CLIENTHEADERSECTION1':
      case 'CLIENTHEADERSECTION2':
      case 'CLIENTHEADERSECTION3':
        valueType = 'clientHeaderFields';
        break;
      case 'COMMUNITYSELFSURVEYASSESSMENT':
        valueType = 'assessments';
        break;
      case 'DEFAULTCHANNELFORREFERRAL':
      case 'COMMUNITYSELFSURVEYASSESSMENTCHANNEL':
        valueType = 'channels';
        break;
      case 'REFERRALPRIVATEENABLEDCHANNELS':
        valueType = 'referralChannelsMulti';
        break;
      case 'SENDINVITEDEFAULTEMAILTEMPLATE':
        valueType = 'sendInviteTemplate';
        break;
      case 'CONFIGURABLEDASHBOARD':
        valueType = 'configurableDashboard';
        break;
      case 'DEFAULTGRIDFILTERPATIENT':
      case 'DEFAULTGRIDFILTERTASK':
      case 'DEFAULTGRIDFILTERCASE':
        valueType = 'gridFilter';
        break;
      default:
        valueType = 'string';
    }
    return valueType;
  }

  /**
   * Check if the setting type is a numeric value.
   *
   * @param aType Setting type.
   * @returns boolean
   */
  private isIntValue(aType: string): boolean
  {
    return aType === 'int';
  }

  private isIntSettingValueMaxValueType(aType: string): boolean
  {
    return aType === 'linkTokenExpiration' || aType === 'optInTokenExpiration' || aType === 'consentExpirationPolicy';
  }

  private addIntSettingMaxValidators(valueValidators: any[]): void
  {
    valueValidators.push(Validators.max(this.intSettingValueMax));
  }

  /**
   * Check if the setting type is an image value.
   *
   * @param aType Setting type.
   * @returns boolean
   */
  private isImageValue(aType: string): boolean
  {
    return aType === 'image';
  }

  /**
   * Check if the setting type is a regular expression pattern.
   *
   * @param key Setting key
   * @returns boolean
   */
  private isPatternRegEx(key: string): boolean
  {
    let returnValue = false;
    switch (key.toUpperCase())
    {
      case 'USERNAMEPATTERN':
      case 'PASSWORDPATTERN':
        returnValue = true;
        break;
    }
    return returnValue;
  }

  /**
   * Check if the setting type is a priority value.
   *
   * @param aType Setting type.
   * @returns boolean
   */
  private isPriorityValue(aType: string): boolean
  {
    return aType === 'priority';
  }

  /**
   * Check if the setting type is a role value.
   *
   * @param aType Setting type.
   * @returns boolean
   */
  private isRolesValue(aType: string): boolean
  {
    return aType === 'roles';
  }

  /**
   * Check if the setting type is a demographic value.
   *
   * @param aType Setting type.
   * @returns boolean
   */
  private isDemographicsValue(aType: string): boolean
  {
    return aType === 'demographics';
  }

  private isRecommendedDemographicsValue(aType: string): boolean
  {
    return aType === 'recommendedDemographics';
  }

  /**
    * Check if the setting type is a insurer field value.
    *
    * @param aType Setting type.
    * @returns boolean
    */
  private isInsuranceFieldValue(aType: string): boolean
  {
    return aType === 'insuranceFields';
  }

  /**
   * Check if the setting type is a login language value.
   *
   * @param aType Setting type.
   * @returns boolean
   */
  private isLoginLanguagesValue(aType: string): boolean
  {
    return aType === 'loginLanguages';
  }

  /**
   * Check if the setting type is a Configurable Dashboard value.
   *
   * @param aType Setting type.
   * @returns boolean
   */
  private isConfigurableDashboardValue(aType: string): boolean
  {
    return aType === 'configurableDashboard';
  }

  private isCommunityDefaultLandingPageValue(aType: string): boolean
  {
    return aType === 'communityDefaultLandingPage';
  }

  /**
   * Check if the setting type is a grid filter value.
   *
   * @param aType Setting type.
   * @returns boolean
   */
  private isGridFilterValue(aType: string): boolean
  {
    return aType === 'gridFilter';
  }

  /**
   * Check if the setting type is a community resource value.
   *
   * @param aType Setting type.
   * @returns boolean
   */
  private isCommunityResourcesValue(aType: string): boolean
  {
    return aType === 'communityResources';
  }

  /**
   * Check if the setting type is a searchable community resource value.
   *
   * @param aType Setting type.
   * @returns boolean
   */
  private isSearchableCommunityResourcesValue(aType: string): boolean
  {
    return aType === 'searchableCommunityResources';
  }

  /**
   * Check if the setting type is a assessments value.
   *
   * @param aType Setting type.
   * @returns boolean
   */
  private isAssessmentsValue(aType: string): boolean
  {
    return aType === 'assessments';
  }

  /**
   * Multi-select assessment ids (stored as comma-separated integers).
   */
  private isAssessmentsMultiValue(aType: string): boolean
  {
    return aType === 'assessmentsMulti';
  }

  private isReferralChannelsMultiValue(aType: string): boolean
  {
    return aType === 'referralChannelsMulti';
  }

  /**
   * Check if the setting type is a channel value.
   *
   * @param aType Setting type.
   * @returns boolean
   */
  private isChannelsValue(aType: string): boolean
  {
    return aType === 'channels';
  }

  /**
   * Check if the setting type is a send invite template value.
   *
   * @param aType Setting type.
   * @returns boolean
   */
  private isSendInviteTemplateValue(aType: string): boolean
  {
    return aType === 'sendInviteTemplate';
  }

  /**
   * Check if the setting type is a team role value.
   *
   * @param aType Setting type.
   * @returns boolean
   */
  private isTeamRolesValue(aType: string): boolean
  {
    return aType === 'teamRoles';
  }

  /**
   * Check if the setting type is a team role value.
   *
   * @param aType Setting type.
   * @returns boolean
   */
  private isTeamRolesMultiValue(aType: string): boolean
  {
    return aType === 'teamRolesMulti';
  }

  /**
   * Check if the setting type is a user value.
   *
   * @param aType Setting type.
   * @returns boolean
   */
  private isProgramLeadUsersValue(aType: string): boolean
  {
    return aType === 'programLeads';
  }

  /**
   * Check if the setting type is a user value.
   *
   * @param aType Setting type.
   * @returns boolean
   */
  private isUsersValue(aType: string): boolean
  {
    return aType === 'users';
  }

  /**
   * Check if the setting type is a scheduler view value.
   *
   * @param aType Setting type.
   * @returns boolean
   */
  private isSchedulerViews(aType: string): boolean
  {
    return aType === 'schedulerViews';
  }

  /**
   * Check if the setting type is a subject identifiers view value.
   *
   * @param aType Setting type.
   * @returns boolean
   */
  private isSubjectIdentifiers(aType: string): boolean
  {
    return aType === 'subjectIdentifiers';
  }

  /**
   * Check if the setting type is a subject identifiers view value.
   *
   * @param aType Setting type.
   * @returns boolean
   */
  private isResourceStatuses(aType: string): boolean
  {
    return aType === 'resourceStatuses';
  }

  private isPayerBillingIdentifier(aType: string): boolean
  {
    return aType === 'payerBillingIdentifier';
  }

  private isConsentType(aType: string): boolean
  {
    return aType === 'consentTypes';
  }

  private isRecordContactFields(aType: string): boolean
  {
    return aType === 'recordContactFieldsSetting';
  }

  private isClientHeaderFields(aType: string): boolean
  {
    return aType === 'clientHeaderFields';
  }

  /**
 * Check if the setting type is a code set view value.
 *
 * @param aType Setting type.
 * @returns boolean
 */
  private isCodeSets(aType: string): boolean
  {
    return aType === 'goalCodeSet';
  }

  /**
   * Check if the value is an array type.
   *
   * @param aType Setting type.
   */
  private isArrayValue(aType: string): boolean
  {
    return this.isRolesValue(aType) || this.isPriorityValue(aType) || this.isSchedulerViews(aType) || this.isSubjectIdentifiers(aType) ||
      this.isProgramLeadUsersValue(aType) || this.isUsersValue(aType) || this.isDemographicsValue(aType) || this.isRecommendedDemographicsValue(aType) ||
      this.isLoginLanguagesValue(aType) || this.isCommunityResourcesValue(aType) || this.isSearchableCommunityResourcesValue(aType) ||
      this.isTeamRolesMultiValue(aType) || this.isAssessmentsMultiValue(aType) || this.isReferralChannelsMultiValue(aType) ||
      this.isConfigurableDashboardValue(aType) || this.isInsuranceFieldValue(aType) ||
      this.isResourceStatuses(aType) || this.isPayerBillingIdentifier(aType) || this.isConsentType(aType) || this.isClientHeaderFields(aType);
  }

  /**
   * Check if the setting type is a boolean value.
   *
   * @param aType Setting type.
   * @returns boolean
   */
  private isBooleanValue(aType: string): boolean
  {
    return aType === 'boolean';
  }

  /**
   * Check if the setting type is a boolean value.
   *
   * @param aType Setting type.
   * @returns boolean
   */
  private isLatitudeLongitudeValue(aType: string): boolean
  {
    return aType === 'latitudeLongitude';
  }

  /**
   * Stored value is normally "lat,lon". Also accept "lat;lon" (e.g. pasted from maps / locale).
   */
  private parseLatitudeLongitudeString(raw: unknown): [number, number]
  {
    if (raw == null || raw === '')
    {
      return [NaN, NaN];
    }
    const s = String(raw).trim();
    const sep = s.includes(';') ? ';' : ',';
    const parts = s.split(sep).map((p) => p.trim());
    return [Number(parts[0]), Number(parts[1])];
  }

  /**
   * Set the selected file.
   *
   * @param file Selected file.
   */
  private setFileFromBlob(file: Blob): void
  {
    const reader = new FileReader();
    reader.addEventListener('load', () =>
    {
      this.imagePreview = reader.result;
      this.selectedFile = reader.result;
    }, false);
    reader.readAsDataURL(file);
  }

  private setGroupedChannels(): void
  {
    // Build the grouped Channels
    const groupedChannels = groupBy(this.channels, [{ field: 'programName' }]);
    this.groupedChannels.forEach(d =>
    {
      const items = sortBy(d.items, ['name']);
      d.items = items;
    });

    this.groupedChannels = sortBy(groupedChannels, ['value']);
  }

  private addMissingReferralChannelsFromValue(value: string): void
  {
    if (!value)
    {
      return;
    }

    value.split(',').forEach((channelId) =>
    {
      const id = parseInt(channelId.trim(), 10);
      if (isNaN(id) || this.allReferralChannels.findIndex(x => x.id === id) !== -1)
      {
        return;
      }

      this.channelService.getMinimalChannelById(id).subscribe({
        next: (lookupChannel) =>
        {
          if (lookupChannel.length === 1)
          {
            lookupChannel.forEach(d =>
            {
              d.programName = d.program.name;
            });
            this.allReferralChannels.push(lookupChannel[0]);
            this.allReferralChannels = sortBy(this.allReferralChannels, ['name']);
            this.referralChannelsFiltered = this.allReferralChannels;
            this.setGroupedReferralChannels();
          }
        }
      });
    });
  }

  private setGroupedReferralChannels(): void
  {
    const groupedReferralChannels = groupBy(this.referralChannelsFiltered, [{ field: 'programName' }]);
    groupedReferralChannels.forEach(d =>
    {
      const items = sortBy(d.items, ['name']);
      d.items = items;
    });

    this.groupedReferralChannels = sortBy(groupedReferralChannels, ['value']);
  }

  private initGridColumns(key: string, filterState: string, filterColumns: string): void
  {
    this.view.columnsConfig = [];
    switch (key.toUpperCase())
    {
      case 'DEFAULTGRIDFILTERPATIENT':
        this.memberListService.setDefaultColumnsSetting(filterState, filterColumns,
          this.appConfigService.getConfig().pageSize, this.patientLabel).then((config: any) =>
          {
            this.view.state = config.state;
            this.view.columnsConfig = config.columns;
          });
        break;
      case 'DEFAULTGRIDFILTERTASK':
        this.taskListService.setDefaultColumnsSetting(filterState, filterColumns,
          this.appConfigService.getConfig().pageSize).then((config: any) =>
          {
            this.view.state = config.state;
            this.view.columnsConfig = config.columns;
          });
        break;
      case 'DEFAULTGRIDFILTERCASE':
        this.caseListService.setDefaultColumnsSetting(filterState, filterColumns,
          this.appConfigService.getConfig().pageSize, this.patientLabel).then((config: any) =>
          {
            this.view.state = config.state;
            this.view.columnsConfig = config.columns;
          });
        break;
    }
  }

  private convertRecordContactToList(recordContactSystemSetting: any)
  {
    const allRecordContactFields = sortBy(this.memberService.recordContact, ['text']);
    allRecordContactFields.forEach(field =>
    {
      this.addRecordContactField(field.text, recordContactSystemSetting.includes(field.value) ? true : false, this.getRecordContactBoolean(recordContactSystemSetting, field.value));
    });
  }

  private convertConsentExpirationPolictToList(settingValue: any)
  {
    const res = settingValue.split(',');

    res.forEach((consent, index) =>
    {
      const consentType = consent.split('|')[0];
      if (consentType !== this.translate.instant('DEFAULT').charAt(0))
      {
        const consentTypeObject = this.consentTypes.find(x => x.code === consentType);
        const unit = consent.split('|')[2];
        const value = Number(consent.split('|')[1]);
        const unitValueField = this.consentExpirationPolicyUnits.filter(x => x.value.charAt(0) === unit)[0].value;
        this.addConsentExpirationPolicy(index, consentTypeObject, value, unitValueField, true);
      }
    });
  }


  private getRecordContactBoolean(recordContactSystemSetting: string, recordContactFieldName: string): boolean
  {
    const pairs = recordContactSystemSetting.split(',');

    for (const pair of pairs)
    {
      const [name, value] = pair.split('|');
      if (name === recordContactFieldName)
      {
        return value === 'true';
      }
    }
  }

  private isSettingRequiredValue(key: string, valueType: string): boolean
  {
    return (
      key !== 'HeaderChannelsUrl' && key !== 'HeaderUrl' && key !== 'PatientContactTeams' && key !== 'ApplicationName' &&
      key !== 'CommunitySelfSurveyAssessment' && key !== 'CommunitySelfSurveyAssessmentChannel' && key !== 'PayerBillingIdentifier' &&
      key !== 'ConsentableOptionsDisabledReadOnlyOnInsert' &&
      key !== 'ClientHeaderSection1' && key !== 'ClientHeadeerSection2' && key !== 'ClientHeaderSection3' &&
      key !== 'ClientSearchOnlyRoles' &&
      !this.isRolesValue(valueType) && !this.isProgramLeadUsersValue(valueType) && !this.isUsersValue(valueType) &&
      !this.isConfigurableDashboardValue(valueType) && !this.isCodeSets(valueType) &&
      !this.isRecordContactFields(valueType) &&
      !this.isRecommendedDemographicsValue(valueType) &&
      !this.isClientHeaderFields(valueType) &&
      !this.isCommunityDefaultLandingPageValue(valueType) &&
      !this.isReferralChannelsMultiValue(valueType)) // We can save empty roles/users/dashboard tiles therefore it is not required
  }

  private scheduleSyncEditWindowHeightFromWindow(): void
  {
    requestAnimationFrame(() =>
    {
      this.syncEditWindowHeightFromWindow();
      requestAnimationFrame(() => this.syncEditWindowHeightFromWindow());
    });
  }

  /** After restore from maximized, Kendo may not match open-time height — reapply saved initial height. */
  private scheduleRestoreInitialWindowHeight(): void
  {
    requestAnimationFrame(() =>
    {
      requestAnimationFrame(() =>
      {
        const h = this.initialEditWindowHeightPx;
        this.editWindowHeightPx = h;
        this.settingEditWindow?.setDimension('height', h);
      });
    });
  }
}
